import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface PermissionContextType {
    permissions: Set<string>;
    scopes: Record<string, string>;
    loading: boolean;
    can: (module: string, action: string) => boolean;
    canAccess: (module: string) => boolean;
    getScope: (module: string) => 'all' | 'zone' | 'team' | 'own';
}

const PermissionContext = createContext<PermissionContextType>({
    permissions: new Set(),
    scopes: {},
    loading: true,
    can: () => false,
    canAccess: () => false,
    getScope: () => 'own',
});

export const usePermission = () => useContext(PermissionContext);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [permissions, setPermissions] = useState<Set<string>>(new Set());
    const [scopes, setScopes] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user && (user as any).permissions) {
            setPermissions(new Set((user as any).permissions));
            setScopes((user as any).scopes || {});
            setLoading(false);
        } else if (!user) {
            setPermissions(new Set());
            setScopes({});
            setLoading(false);
        }
    }, [user]);

    const can = (module: string, action: string): boolean => {
        return permissions.has(`${module}:${action}`);
    };

    const canAccess = (module: string): boolean => {
        // Check if user has ANY permission for this module
        return Array.from(permissions).some(p => p.startsWith(`${module}:`));
    };

    const getScope = (module: string): 'all' | 'zone' | 'team' | 'own' => {
        return (scopes[module] as any) || 'own';
    };

    return (
        <PermissionContext.Provider value={{ permissions, scopes, loading, can, canAccess, getScope }}>
            {children}
        </PermissionContext.Provider>
    );
};
